import { useTranslation } from "react-i18next";
import { useConfirm } from "@/shared/hooks/use-confirm";
import { Plus, Pencil, Trash2, FileQuestion, MessageSquare, Mic } from "lucide-react";
import { Badge } from "@/shared/ui/badge";
import { Card, CardContent } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { ScenarioFormDialog, type ScenarioFormData } from "./ScenarioFormDialog";
import type { CoachingScenarioItem, ScenarioCreateRequest } from "../../api/coachingService";
import { difficultyLabel, normalizeDifficulty } from "./helpers";

function diffBadge(d: string) {
  const n = normalizeDifficulty(d);
  const label = difficultyLabel(d);
  if (n === "easy")
    return (
      <Badge variant="secondary" className="text-2xs">
        {label}
      </Badge>
    );
  if (n === "medium")
    return <Badge className="bg-warning/10 text-warning border-warning/20 text-2xs">{label}</Badge>;
  return (
    <Badge className="bg-destructive/10 text-destructive border-destructive/20 text-2xs">
      {label}
    </Badge>
  );
}

type Props = {
  scenarios: CoachingScenarioItem[];
  tagNameMap: Map<string, string>;
  formOpen: boolean;
  editingScenario: CoachingScenarioItem | null;
  initialForm: ScenarioFormData;
  onOpenCreate: () => void;
  onEdit: (s: CoachingScenarioItem) => void;
  onCloseForm: () => void;
  onSubmit: (data: ScenarioCreateRequest) => void;
  isSubmitting: boolean;
  onDelete: (id: number) => void;
  isDeleting: boolean;
  /** MANAGER+ 写门(对齐后端 PD-2:场景写 hasRole TENANT_MANAGER);STAFF 隐藏写按钮。 */
  canManage: boolean;
};

export function ScenariosTab({
  canManage,
  scenarios,
  tagNameMap,
  formOpen,
  editingScenario,
  initialForm,
  onOpenCreate,
  onEdit,
  onCloseForm,
  onSubmit,
  isSubmitting,
  onDelete,
  isDeleting,
}: Props) {
  const { t } = useTranslation("coaching");
  const confirm = useConfirm();
  return (
    <div className="space-y-4 mt-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {t("scenariosTab.totalTemplate", { count: scenarios.length })}
        </p>
        {canManage && (
          <Button size="sm" onClick={onOpenCreate}>
            <Plus className="h-4 w-4 mr-1" />
            {t("scenariosTab.newScenario")}
          </Button>
        )}
      </div>

      <ScenarioFormDialog
        open={formOpen || editingScenario !== null}
        onClose={onCloseForm}
        initial={initialForm}
        onSubmit={onSubmit}
        isPending={isSubmitting}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {scenarios.map((sc, i) => (
          <Card
            key={sc.id}
            className="shadow-sm hover:shadow-md transition-shadow fx-hover-lift"
            style={{ animationDelay: `${Math.min(i, 7) * 30}ms` }}
          >
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm">{sc.name}</h3>
                <div className="flex items-center gap-1.5">
                  <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                    {sc.mode === "qa_drill" ? (
                      <FileQuestion className="h-3 w-3" />
                    ) : (
                      <MessageSquare className="h-3 w-3" />
                    )}
                    {sc.mode === "qa_drill" ? "知识问答" : "情景对练"}
                  </span>
                  {sc.interactionMode === "voice" && (
                    <span className="inline-flex items-center gap-1 rounded bg-info/10 px-1.5 py-0.5 text-xs text-info">
                      <Mic className="h-3 w-3" />
                      语音
                    </span>
                  )}
                  {sc.mode !== "qa_drill" && sc.difficulty && diffBadge(sc.difficulty)}
                </div>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{sc.description}</p>
              <div className="flex flex-wrap gap-1.5">
                {/* V31:positions 后端存逗号分隔字符串(老表 VARCHAR(255)),与下面 knowledgeTags 同款拆法 */}
                {(sc.positions ?? "")
                  .split(",")
                  .map((p) => p.trim())
                  .filter(Boolean)
                  .map((p) => (
                    <Badge
                      key={p}
                      variant="secondary"
                      className="text-2xs font-normal normal-case tracking-normal"
                    >
                      {p}
                    </Badge>
                  ))}
                {sc.knowledgeTags &&
                  sc.knowledgeTags
                    .split(",")
                    .filter(Boolean)
                    .map((tag) => (
                      <Badge
                        key={tag}
                        variant="outline"
                        className="text-2xs font-normal normal-case tracking-normal text-info border-info/20"
                      >
                        {tagNameMap.get(tag.trim()) || tag.trim()}
                      </Badge>
                    ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-2 rounded-lg bg-muted/50 text-center">
                  <p className="text-lg font-bold text-foreground">{sc.maxRounds ?? 8}</p>
                  <p className="text-2xs text-muted-foreground">
                    {t("scenariosTab.maxRoundsLabel")}
                  </p>
                </div>
                {/* 1006:没自定义维度时 ai 按默认模板(销售类 5 项)评估 —— 卡片别写「0」 */}
                {(sc.dimensions?.length ?? 0) > 0 ? (
                  <div className="p-2 rounded-lg bg-muted/50 text-center">
                    <p className="text-lg font-bold text-foreground">{sc.dimensions?.length}</p>
                    <p className="text-2xs text-muted-foreground">
                      {t("scenariosTab.dimensionsLabel")}
                    </p>
                  </div>
                ) : sc.mode === "qa_drill" ? (
                  // 知识问答闯关按每题要点卡判分,不走评估维度
                  <div className="p-2 rounded-lg bg-muted/50 text-center">
                    <p className="text-lg font-bold text-foreground">
                      {t("scenariosTab.qaPointsValue")}
                    </p>
                    <p className="text-2xs text-muted-foreground">
                      {t("scenariosTab.qaPointsLabel")}
                    </p>
                  </div>
                ) : (
                  <div
                    className="p-2 rounded-lg bg-muted/50 text-center"
                    title={t("scenariosTab.dimensionsDefaultHint")}
                  >
                    <p className="text-lg font-bold text-foreground">
                      {t("scenariosTab.dimensionsDefault")}
                    </p>
                    <p className="text-2xs text-muted-foreground">
                      {t("scenariosTab.dimensionsLabel")}
                    </p>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs"
                  onClick={() => onEdit(sc)}
                >
                  <Pencil className="h-3 w-3 mr-1" />
                  {t("scenariosTab.edit")}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  disabled={isDeleting}
                  onClick={async () => {
                    if (
                      await confirm({
                        title: t("scenariosTab.confirmDelete", { name: sc.name }),
                        confirmText: t("common.delete", { defaultValue: "删除" }),
                        variant: "destructive",
                      })
                    ) {
                      onDelete(sc.id);
                    }
                  }}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
